import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  IconButton,
  Fab,
  Badge,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import {
  Add,
  Delete,
  Circle,
  SmartToy,
  Agriculture,
  Assessment,
  TrendingUp,
  Business,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';
import { SURFACE_TOKENS, CHAT_SURFACE_PATTERNS } from '../../../design-system/tokens/surfaces';
import { TYPOGRAPHY_TOKENS } from '../../../design-system/tokens/typography';
import { useChatContext } from '../contexts/ChatContext';
import { Organization, ChatListViewProps } from '../types/chatTypes';
import { Farm } from '../../../services/farm/farmService';
import { EnhancedNewChatModal } from '../../organizations/components/EnhancedNewChatModal';
import { chatSessionService } from '../services/ChatSessionService';
import {
  getOrganizationColor,
  getOrganizationIcon,
  BRAND_COLORS,
  NEUTRAL_COLORS,
  SPACING,
  AVATAR_SIZES,
  COMMON_STYLES,
  STATUS_COLORS,
  BORDER_RADIUS,
} from '../styles/constants';
import { CompleteProfileModal } from '../../profile/components/CompleteProfileModal';
import { useAuthContext } from '../../../contexts/AuthContext';
import { Tooltip, TooltipProps, tooltipClasses } from '@mui/material';
import { styled } from '@mui/material/styles';

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
    pointerEvents: 'auto',
  },
}));

// Helper functions moved to constants file

const formatTime = (date: Date) => {
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
  
  if (diffInMinutes < 1) return 'agora';
  if (diffInMinutes < 60) return `${diffInMinutes}m`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
  return `${Math.floor(diffInMinutes / 1440)}d`;
};

export const ChatListView: React.FC<ChatListViewProps> = ({}) => {
  const { t } = useTranslation();
  const { sessions, organizations, activeSessionId, setActiveSession, deleteSession, createSession } = useChatContext();
  const { authState: { hasCompleteProfile } } = useAuthContext();
  const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [hoveredSession, setHoveredSession] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);
  const [expandedOrganizations, setExpandedOrganizations] = useState<Set<string>>(new Set());

  const handleSessionSelect = (sessionId: string) => {
    setActiveSession(sessionId);
  };

  const handleNewChatClick = async () => {
// No need to load organizations since they are already available through context
    setIsNewChatModalOpen(true);
  };

  const handleNewChatModalClose = () => {
    setIsNewChatModalOpen(false);
  };

  const handleDeleteSession = (sessionId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setSessionToDelete(sessionId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (sessionToDelete) {
      try {
        await deleteSession(sessionToDelete);
        setDeleteDialogOpen(false);
        setSessionToDelete(null);
      } catch (error) {
        console.error('Erro ao deletar sessão:', error);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSessionToDelete(null);
  };

  const getOrganization = (orgId: string) => {
    const found = (organizations || []).find(org => 
      org.id === orgId || 
      String(org.id) === String(orgId) ||
      (org as any)._key === orgId
    );
    
    return found;
  };

  const toggleOrganizationExpansion = (orgId: string) => {
    setExpandedOrganizations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orgId)) {
        newSet.delete(orgId);
      } else {
        newSet.add(orgId);
      }
      return newSet;
    });
  };

  // Agrupar sessões por organização
  const groupedSessions = React.useMemo(() => {
    const groups: { [key: string]: any[] } = {};
    
    sessions.forEach(session => {
      const orgId = session.organization_id || 'unknown';
      if (!groups[orgId]) {
        groups[orgId] = [];
      }
      groups[orgId].push(session);
    });

    // Ordenar sessões dentro de cada grupo por data
    Object.keys(groups).forEach(orgId => {
      groups[orgId].sort((a, b) => 
        new Date(b.last_used || b.updated_at || b.created_at).getTime() - 
        new Date(a.last_used || a.updated_at || a.created_at).getTime()
      );
    });

    // Ordenar grupos pela sessão mais recente
    const sortedGroups = Object.entries(groups).sort(([, sessionsA], [, sessionsB]) => {
      const latestA = sessionsA[0];
      const latestB = sessionsB[0];
      return new Date(latestB.last_used || latestB.updated_at || latestB.created_at).getTime() - 
             new Date(latestA.last_used || latestA.updated_at || latestA.created_at).getTime();
    });

    return sortedGroups;
  }, [sessions]);

  return (
    <Box sx={{ 
      height: '100%', 
      width: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      position: 'relative',
      overflow: 'hidden' // Add this to prevent outer scroll
    }}>
      <EnhancedNewChatModal
        isOpen={isNewChatModalOpen}
        onClose={handleNewChatModalClose}
        onChatCreated={async (organization: any) => {
           try {
             let sessionId: string;
             
             const orgId = (organization as any)._key || organization.id;
             sessionId = await createSession(orgId, `Conversa - ${organization.name}`, organization);
             
             handleSessionSelect(sessionId);
              setIsNewChatModalOpen(false);
           } catch (error) {
             console.error('Erro ao criar sessão:', error);
             // Ainda fecha o modal mesmo com erro
             setIsNewChatModalOpen(false);
           }
         }}
       />
      {/* Header */}
      <Box
        sx={{
          ...CHAT_SURFACE_PATTERNS.header.primary,
          p: SPACING.lg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          flexShrink: 0 // Prevent header from shrinking
        }}
      >
        <Box>
          <Typography 
            variant="h6" 
            sx={{ 
              fontSize: '1.125rem',
              fontWeight: TYPOGRAPHY_TOKENS.fontWeights.semibold,
              color: DESIGN_TOKENS.colors.text.primary,
              lineHeight: 1.25
            }}
          >
            {t('chat.conversationsTitle')}
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '0.875rem',
              color: DESIGN_TOKENS.colors.text.secondary,
              fontWeight: TYPOGRAPHY_TOKENS.fontWeights.normal
            }}
          >
            {t('chat.conversationCount', { count: sessions.length })}
          </Typography>
        </Box>
      </Box>

      {/* Thread List - This should be the scrollable area */}
      <Box sx={{ 
        flex: 1, 
        width: '100%', 
        overflow: 'hidden', // Parent container prevents outer scroll
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0, // Critical for flex children
        backgroundColor: DESIGN_TOKENS.colors.background.paper
      }}>
        {sessions.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              p: 4,
              textAlign: 'center',
              overflow: 'auto', // Allow scrolling if welcome content overflows
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(255,255,255,0.1)',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255,255,255,0.3)',
                borderRadius: '3px',
              },
            }}
          >
            {/* AI Welcome Section */}
            <Fade in timeout={800}>
              <Box
                sx={{
                  ...CHAT_SURFACE_PATTERNS.welcomeSection.default,
                  mb: 4,
                  p: 4,
                  borderRadius: SURFACE_TOKENS.borderRadius.xl,
                  background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primary[50]} 0%, ${DESIGN_TOKENS.colors.background.paper} 100%)`,
                  border: `1px solid ${DESIGN_TOKENS.colors.primary[200]}`,
                  boxShadow: `0 4px 12px rgba(22, 163, 74, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)`,
                  maxWidth: 420,
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: `linear-gradient(90deg, ${DESIGN_TOKENS.colors.primary[500]} 0%, ${DESIGN_TOKENS.colors.primary[300]} 100%)`,
                  },
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px rgba(22, 163, 74, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)`,
                  }
                }}
              >
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Avatar
                    sx={{
                      width: 72,
                      height: 72,
                      bgcolor: DESIGN_TOKENS.colors.primary[500],
                      mb: 2,
                      mx: 'auto',
                      boxShadow: `0 4px 12px rgba(22, 163, 74, 0.25)`,
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: `0 6px 16px rgba(22, 163, 74, 0.35)`,
                      }
                    }}
                  >
                    <SmartToy sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Typography variant="h5" sx={{ 
                    fontSize: '1.25rem',
                    fontWeight: TYPOGRAPHY_TOKENS.fontWeights.bold, 
                    mb: 1, 
                    color: DESIGN_TOKENS.colors.primary[800],
                    lineHeight: 1.2,
                    letterSpacing: '-0.01em'
                  }}>
                    Bem-vindo ao MUU AI
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    fontSize: '0.9rem',
                    color: DESIGN_TOKENS.colors.text.primary, 
                    mb: 3, 
                    lineHeight: 1.6,
                    fontWeight: TYPOGRAPHY_TOKENS.fontWeights.medium,
                    maxWidth: '280px',
                    mx: 'auto'
                  }}>
                    Seu assistente inteligente especializado em agropecuária. Posmo ajudá-lo com:
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr',
                  gap: 1.5, 
                  mb: 3 
                }}>
                  <Chip
                    icon={<Agriculture />}
                    label="Manejo de pastagens"
                    variant="outlined"
                    size="small"
                    sx={{ 
                      ...CHAT_SURFACE_PATTERNS.chip.feature,
                      justifyContent: 'flex-start',
                      fontSize: '0.75rem',
                      fontWeight: TYPOGRAPHY_TOKENS.fontWeights.medium,
                      borderColor: DESIGN_TOKENS.colors.primary[200],
                      color: DESIGN_TOKENS.colors.primary[800],
                      backgroundColor: DESIGN_TOKENS.colors.primary[50],
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: DESIGN_TOKENS.colors.primary[100],
                        borderColor: DESIGN_TOKENS.colors.primary[300],
                        transform: 'translateY(-1px)',
                        boxShadow: `0 2px 8px rgba(22, 163, 74, 0.15)`,
                      },
                      '& .MuiChip-icon': {
                        color: DESIGN_TOKENS.colors.primary[600],
                        fontSize: '1rem',
                      },
                    }}
                  />
                  <Chip
                    icon={<Assessment />}
                    label="Análise e relatórios"
                    variant="outlined"
                    size="small"
                    sx={{ 
                      ...CHAT_SURFACE_PATTERNS.chip.feature,
                      justifyContent: 'flex-start',
                      fontSize: '0.75rem',
                      fontWeight: TYPOGRAPHY_TOKENS.fontWeights.medium,
                      borderColor: DESIGN_TOKENS.colors.primary[200],
                      color: DESIGN_TOKENS.colors.primary[800],
                      backgroundColor: DESIGN_TOKENS.colors.primary[50],
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: DESIGN_TOKENS.colors.primary[100],
                        borderColor: DESIGN_TOKENS.colors.primary[300],
                        transform: 'translateY(-1px)',
                        boxShadow: `0 2px 8px rgba(22, 163, 74, 0.15)`,
                      },
                      '& .MuiChip-icon': {
                        color: DESIGN_TOKENS.colors.primary[600],
                        fontSize: '1rem',
                      },
                    }}
                  />
                  <Chip
                    icon={<TrendingUp />}
                    label="Otimização"
                    variant="outlined"
                    size="small"
                    sx={{ 
                      ...CHAT_SURFACE_PATTERNS.chip.feature,
                      justifyContent: 'flex-start',
                      fontSize: '0.75rem',
                      fontWeight: TYPOGRAPHY_TOKENS.fontWeights.medium,
                      borderColor: DESIGN_TOKENS.colors.primary[200],
                      color: DESIGN_TOKENS.colors.primary[800],
                      backgroundColor: DESIGN_TOKENS.colors.primary[50],
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: DESIGN_TOKENS.colors.primary[100],
                        borderColor: DESIGN_TOKENS.colors.primary[300],
                        transform: 'translateY(-1px)',
                        boxShadow: `0 2px 8px rgba(22, 163, 74, 0.15)`,
                      },
                      '& .MuiChip-icon': {
                        color: DESIGN_TOKENS.colors.primary[600],
                        fontSize: '1rem',
                      },
                    }}
                  />
                  <Chip
                    icon={<Business />}
                    label="Gestão organizacional"
                    variant="outlined"
                    size="small"
                    sx={{ 
                      ...CHAT_SURFACE_PATTERNS.chip.feature,
                      justifyContent: 'flex-start',
                      fontSize: '0.75rem',
                      fontWeight: TYPOGRAPHY_TOKENS.fontWeights.medium,
                      borderColor: DESIGN_TOKENS.colors.primary[200],
                      color: DESIGN_TOKENS.colors.primary[700],
                      backgroundColor: DESIGN_TOKENS.colors.primary[50],
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: DESIGN_TOKENS.colors.primary[100],
                        borderColor: DESIGN_TOKENS.colors.primary[300],
                        transform: 'translateY(-1px)',
                        boxShadow: `0 2px 8px rgba(22, 163, 74, 0.15)`,
                      },
                      '& .MuiChip-icon': {
                        color: DESIGN_TOKENS.colors.primary[600],
                        fontSize: '1rem',
                      },
                    }}
                  />
                </Box>
                
                <Box sx={{ 
                  textAlign: 'center',
                  p: 2,
                  borderRadius: SURFACE_TOKENS.borderRadius.md,
                  backgroundColor: DESIGN_TOKENS.colors.primary[50],
                  border: `1px solid ${DESIGN_TOKENS.colors.primary[100]}`,
                }}>
                  <Typography variant="caption" sx={{ 
                    fontSize: '0.8rem',
                    color: DESIGN_TOKENS.colors.primary[700], 
                    fontWeight: TYPOGRAPHY_TOKENS.fontWeights.medium,
                    lineHeight: 1.4,
                    display: 'block'
                  }}>
                    Inicie uma conversa para começar a usar a IA especializada
                  </Typography>
                </Box>
              </Box>
            </Fade>

            {/* Call to Action removida conforme solicitado */}
          </Box>
        ) : (
          <List sx={{ 
            p: 0, 
            flex: 1, // Take remaining space
            width: '100%', 
            overflow: 'auto', // This enables scrolling for the chat list
            minHeight: 0, // Important for flex children
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255,255,255,0.1)',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255,255,255,0.3)',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgba(255,255,255,0.5)',
            },
          }}>
            {groupedSessions.map(([orgId, orgSessions], groupIndex) => {
              const organization = getOrganization(orgId);
              const orgColor = getOrganizationColor(organization || orgSessions[0]?.organization_type || 'other');
              const isExpanded = expandedOrganizations.has(orgId);
              const orgName = organization?.name || `Organização ${orgId}`;
              
              return (
                <Fade key={orgId} in timeout={300 + groupIndex * 100}>
                  <Box>
                    {/* Cabeçalho da Organização */}
                    <ListItem 
                      disablePadding
                      sx={{ position: 'relative' }}
                    >
                      <ListItemButton
                        onClick={() => toggleOrganizationExpansion(orgId)}
                        sx={{
                          backgroundColor: 'white',
                          borderRadius: '12px',
                          mb: 2,
                          border: 'none',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                          padding: '16px',
                          '&:hover': {
                            backgroundColor: NEUTRAL_COLORS.gray50,
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            transform: 'translateY(-1px)',
                          },
                          transition: 'all 0.2s ease-in-out',
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              width: 48,
                              height: 48,
                              background: `linear-gradient(135deg, ${orgColor}15, ${orgColor}25)`,
                              color: orgColor,
                              fontSize: '1.2rem',
                              fontWeight: 600,
                              border: `2px solid ${orgColor}30`,
                              boxShadow: `0 2px 4px ${orgColor}20`,
                            }}
                          >
                            <Box
                              component="img"
                              src="/Fazenda.svg"
                              alt="Fazenda"
                              sx={{
                                width: 28,
                                height: 28,
                              }}
                            />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography
                              variant="h6"
                              sx={{
                                color: NEUTRAL_COLORS.gray900,
                                fontWeight: 700,
                                fontSize: '1.1rem',
                                lineHeight: 1.3,
                                mb: 0.5,
                              }}
                            >
                              {orgName}
                            </Typography>
                          }
                          secondary={
                            <Typography
                              variant="body2"
                              sx={{
                                color: NEUTRAL_COLORS.gray500,
                                fontWeight: 500,
                                fontSize: '0.85rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <Circle sx={{ fontSize: '4px', color: orgColor }} />
                              {t('chat.conversationCount', { count: orgSessions.length })}
                            </Typography>
                          }
                        />
                        <IconButton
                            size="small"
                            sx={{ 
                              color: orgColor,
                              backgroundColor: `${orgColor}10`,
                              borderRadius: '8px',
                              padding: '8px',
                              '&:hover': {
                                backgroundColor: `${orgColor}20`,
                                transform: 'scale(1.05)',
                              },
                              transition: 'all 0.2s ease-in-out',
                            }}
                          >
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                      </ListItemButton>
                    </ListItem>

                    {/* Conversas da Organização */}
                    {isExpanded && (
                      <Fade in={isExpanded}>
                        <Box sx={{ 
                          mt: 2, 
                          mb: 1,
                          backgroundColor: NEUTRAL_COLORS.gray50,
                          borderRadius: '8px',
                          padding: '12px 8px',
                          border: `1px solid ${NEUTRAL_COLORS.gray100}`,
                        }}>
                          {orgSessions.map((session, sessionIndex) => (
                            <Fade key={session.session_id} in timeout={200 + sessionIndex * 50}>
                              <Box>
                                <ListItem 
                                  disablePadding
                                  onMouseEnter={() => setHoveredSession(session.session_id)}
                                  onMouseLeave={() => setHoveredSession(null)}
                                  sx={{ position: 'relative' }}
                                >
                                  <ListItemButton
                                    onClick={() => handleSessionSelect(session.session_id)}
                                    selected={activeSessionId === session.session_id}
                                    sx={{
                                      ...CHAT_SURFACE_PATTERNS.listItem.default,
                                      py: SPACING.sm,
                                      px: SPACING.md,
                                      borderRadius: '8px',
                                      mb: 1,
                                      ml: 3,
                                      mr: 1,
                                      backgroundColor: activeSessionId === session.session_id ? BRAND_COLORS.primaryLight : 'white',
                                      border: activeSessionId === session.session_id ? `2px solid ${BRAND_COLORS.primary}` : `1px solid ${NEUTRAL_COLORS.gray200}`,
                                      boxShadow: activeSessionId === session.session_id ? `0 2px 8px ${BRAND_COLORS.primary}20` : '0 1px 3px rgba(0, 0, 0, 0.05)',
                                      '&:hover': {
                                        ...CHAT_SURFACE_PATTERNS.listItem.hover,
                                        backgroundColor: activeSessionId === session.session_id ? BRAND_COLORS.primaryLight : NEUTRAL_COLORS.gray50,
                                        borderColor: BRAND_COLORS.primary,
                                        boxShadow: `0 2px 8px ${BRAND_COLORS.primary}15`,
                                        transform: 'translateX(4px)',
                                      },
                                      '&.Mui-selected': {
                                        ...CHAT_SURFACE_PATTERNS.listItem.active,
                                        backgroundColor: BRAND_COLORS.primaryLight,
                                        borderRight: `3px solid ${BRAND_COLORS.primary}`,
                                        '&:hover': {
                                          backgroundColor: BRAND_COLORS.primaryLight,
                                        },
                                      },
                                      transition: 'all 0.2s ease-in-out',
                                    }}
                                  >
                                    <ListItemAvatar>
                                      <Badge
                                        badgeContent={0}
                                        color="error"
                                        invisible={true}
                                      >
                                        <Avatar
                                          sx={{
                                            width: 32,
                                            height: 32,
                                            background: `linear-gradient(135deg, ${orgColor}15, ${orgColor}25)`,
                                            color: orgColor,
                                            fontSize: '0.9rem',
                                            border: `1px solid ${orgColor}40`,
                                            boxShadow: `0 1px 3px ${orgColor}20`,
                                          }}
                                        >
                                          <Box
                                            component="img"
                                            src="/Fazenda.svg"
                                            alt="Fazenda"
                                            sx={{
                                              width: 28,
                                              height: 28,
                                            }}
                                          />
                                        </Avatar>
                                      </Badge>
                                    </ListItemAvatar>
                                    <ListItemText
                                      primaryTypographyProps={{ component: 'div' }}
                                      secondaryTypographyProps={{ component: 'div' }}
                                      primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                          <Typography
                                            component="span"
                                            variant="subtitle2"
                                            sx={{
                                              fontSize: '0.9rem',
                                              fontWeight: 600,
                                              color: activeSessionId === session.session_id ? BRAND_COLORS.primary : NEUTRAL_COLORS.gray800,
                                              flex: 1,
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                              lineHeight: 1.4,
                                            }}
                                          >
                                            {session.title || `Conversa ${session.session_id?.slice(-4)}`}
                                          </Typography>
                                          <Typography
                                            component="span"
                                            variant="caption"
                                            sx={{ 
                                              fontSize: '0.7rem',
                                              color: NEUTRAL_COLORS.gray500,
                                              fontWeight: TYPOGRAPHY_TOKENS.fontWeights.normal,
                                              flexShrink: 0,
                                            }}
                                          >
                                            {session.updated_at ? formatTime(new Date(session.updated_at)) : ''}
                                          </Typography>
                                        </Box>
                                      }
                                      secondary={
                                        <Typography
                                          component="span"
                                          variant="caption"
                                          sx={{
                                            fontSize: '0.75rem',
                                            color: NEUTRAL_COLORS.gray500,
                                            fontWeight: TYPOGRAPHY_TOKENS.fontWeights.normal,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            display: 'block',
                                            lineHeight: 1.3,
                                          }}
                                        >
                                          {session.last_message || 'Nenhuma mensagem ainda'}
                                        </Typography>
                                      }
                                    />
                                  </ListItemButton>
                                  {hoveredSession === session.session_id && (
                                    <Fade in timeout={150}>
                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          right: 8,
                                          top: '50%',
                                          transform: 'translateY(-50%)',
                                          zIndex: 10,
                                        }}
                                      >
                                        <IconButton
                                          size="small"
                                          onClick={(e) => handleDeleteSession(session.session_id, e)}
                                          sx={{
                                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                                            color: NEUTRAL_COLORS.gray600,
                                            border: `1px solid ${NEUTRAL_COLORS.gray300}`,
                                            borderRadius: BORDER_RADIUS.sm,
                                            width: 28,
                                            height: 28,
                                            backdropFilter: 'blur(8px)',
                                            '&:hover': {
                                              bgcolor: STATUS_COLORS.error,
                                              color: 'white',
                                              transform: 'scale(1.05)',
                                              boxShadow: `0 4px 12px ${STATUS_COLORS.error}40`,
                                              border: `1px solid ${STATUS_COLORS.error}`,
                                            },
                                            '&:active': {
                                              transform: 'scale(0.95)',
                                            },
                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                          }}
                                        >
                                          <Delete sx={{ fontSize: 14 }} />
                                        </IconButton>
                                      </Box>
                                    </Fade>
                                  )}
                                </ListItem>
                              </Box>
                            </Fade>
                          ))}
                        </Box>
                      </Fade>
                    )}
                  </Box>
                </Fade>
              );
            })}
            </List>
        )}
      </Box>

      {/* Fixed Add Button - Always visible in viewport */}
      <HtmlTooltip
        title={
          !hasCompleteProfile ? (
            <Box>
              <Typography color="inherit" variant="subtitle2" fontWeight="bold">Completar Perfil</Typography>
              <Typography variant="caption" display="block" paragraph>
                Para criar uma nova fazenda, precisamos que você complete seu cadastro primeiro.
              </Typography>
              <Button 
                size="small" 
                variant="outlined" 
                fullWidth 
                onClick={(e) => {
                  e.stopPropagation();
                  setShowProfileModal(true);
                }}
              >
                Completar Agora
              </Button>
            </Box>
          ) : ""
        }
        disableHoverListener={hasCompleteProfile}
        disableFocusListener={hasCompleteProfile}
        disableTouchListener={hasCompleteProfile}
        arrow
        placement="left"
        disableInteractive={false}
      >
        <Box sx={{ 
          position: 'fixed', 
          bottom: 16, 
          left: { xs: 'auto', md: 315 }, 
          right: { xs: 16, md: 'auto' },
          zIndex: 1000 
        }}>
          <Fab
            color="primary"
            onClick={() => {
              if (!hasCompleteProfile) {
                setShowProfileModal(true);
                return;
              }
              handleNewChatClick();
            }}
            sx={{
              ...COMMON_STYLES.primaryButton,
              width: 56,
              height: 56,
              backgroundColor: hasCompleteProfile ? DESIGN_TOKENS.colors.primary[500] : DESIGN_TOKENS.colors.neutral[300],
              boxShadow: `0 4px 16px rgba(22, 163, 74, 0.25)`,
              '&:hover': {
                backgroundColor: hasCompleteProfile ? DESIGN_TOKENS.colors.primary[600] : DESIGN_TOKENS.colors.neutral[300],
                transform: hasCompleteProfile ? 'translateY(-2px) scale(1.05)' : 'none',
                boxShadow: `0 8px 24px rgba(22, 163, 74, 0.35)`,
              },
              '&:active': {
                transform: 'translateY(0) scale(1.02)',
              },
              transition: 'all 0.3s ease-in-out',
              cursor: hasCompleteProfile ? 'pointer' : 'not-allowed',
            }}
          >
            <Add sx={{ fontSize: 24 }} />
          </Fab>
        </Box>
      </HtmlTooltip>

      {/* Complete Profile Modal */}
      <CompleteProfileModal 
        open={showProfileModal} 
        onClose={() => setShowProfileModal(false)}
        onSuccess={() => setShowProfileModal(false)}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        PaperProps={{
          sx: {
            borderRadius: BORDER_RADIUS.md,
            minWidth: 320,
            maxWidth: 360,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1rem',
            fontWeight: 600,
            color: NEUTRAL_COLORS.gray900,
            pb: SPACING.xs,
          }}
        >
          {t('chat.deleteConversation')}
        </DialogTitle>
        <DialogContent sx={{ pt: SPACING.xs, pb: SPACING.sm }}>
          <DialogContentText sx={{ color: NEUTRAL_COLORS.gray700, fontSize: '0.95rem' }}>
            {t('chat.deleteConfirmation')}
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            p: SPACING.md,
            gap: SPACING.sm,
          }}
        >
          <Button
            onClick={handleCancelDelete}
            variant="text"
            sx={{ color: NEUTRAL_COLORS.gray800, borderRadius: BORDER_RADIUS.md, px: SPACING.lg }}
          >
            {t('chat.buttons.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              bgcolor: DESIGN_TOKENS.colors.primary[500],
              '&:hover': { bgcolor: DESIGN_TOKENS.colors.primary[600] },
              borderRadius: BORDER_RADIUS.md,
              px: SPACING.lg,
            }}
          >
            {t('chat.buttons.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
